import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { trpc } from '~/api/trpc';
import { Button } from '~/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '~/components/ui/dialog';
import { Textarea } from '~/components/ui/textarea';
import { Label } from '~/components/ui/label';

type AddNoteDialogProps = {
    customerId: number;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: () => void;
};

const AddNoteDialog = ({ customerId, open, onOpenChange, onSuccess }: AddNoteDialogProps) => {
    const [content, setContent] = useState('');
    const utils = trpc.useUtils();

    const createNote = trpc.medicalRecord.createNote.useMutation({
        onSuccess: () => {
            utils.medicalRecord.getByCustomerId.invalidate({ customerId });
            toast.success('Notiz wurde hinzugefügt');
            setContent('');
            onOpenChange(false);
            onSuccess?.();
        },
        onError: (error) => {
            toast.error(`Fehler beim Erstellen der Notiz: ${error.message}`);
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!content.trim()) {
            toast.error('Bitte geben Sie einen Text ein');
            return;
        }

        createNote.mutate({
            customerId,
            content: content.trim(),
        });
    };

    const handleOpenChange = (newOpen: boolean) => {
        if (!newOpen) {
            setContent('');
        }
        onOpenChange(newOpen);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <form onSubmit={handleSubmit}>
                    <DialogHeader>
                        <DialogTitle>Notiz hinzufügen</DialogTitle>
                        <DialogDescription>
                            Fügen Sie eine neue Notiz zur Patientenakte hinzu.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="py-4">
                        <Label htmlFor="note-content">Notiz *</Label>
                        <Textarea
                            id="note-content"
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder="Notiz eingeben..."
                            className="mt-2 min-h-[120px]"
                            disabled={createNote.isPending}
                        />
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleOpenChange(false)}
                            disabled={createNote.isPending}
                        >
                            Abbrechen
                        </Button>
                        <Button type="submit" disabled={createNote.isPending || !content.trim()}>
                            {createNote.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Speichern
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddNoteDialog;
